import { useEffect, type FC } from 'react';

import { useLocation } from 'react-router';

import { InfoMelding, WarnMelding } from '../../types/helsenorge.helseregistre.internalapi.entities.enums';
import { HelseregistreJSProject } from '../../types/project-name-constant';
import type { HNHelseregistreJS } from '../../types/Resources';

import Button from '@helsenorge/designsystem-react/components/Button';
import NotificationPanel from '@helsenorge/designsystem-react/components/NotificationPanel';
import Spacer from '@helsenorge/designsystem-react/components/Spacer';

import PageLoader from '@helsenorge/core-framework/page-loader';
import PageRoot from '@helsenorge/core-framework/pageroot';
import HelpExpanderStandalone from '@helsenorge/core-framework/prosesshjelp/components/helpexpanderstandalone';
import { isAuthorized } from '@helsenorge/framework-utils/hn-authorize';
import { getPath } from '@helsenorge/framework-utils/hn-page';

import { Innloggingsknapp } from './innloggingsknapp';
import { fetchRegisterliste, fetchRegisterlistePortal } from './registre';
import RegistreComponent from './registre-component/registre-component';
import { useResources } from '../../resources';
import { useAppDispatch, useAppSelector } from '../store/hooks';

const RegistreContainer: FC = () => {
  const locationHook = useLocation();

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (isAuthorized()) {
      dispatch(fetchRegisterliste({ retryInnsynOrkestrator: false }));
    } else {
      dispatch(fetchRegisterlistePortal());
    }
  }, [dispatch]);

  const resources = useResources<HNHelseregistreJS>(HelseregistreJSProject);
  const { error, loading, registerliste, warn, info, retryInnsynOrkestrator } = useAppSelector(state => state.registre);

  const location = `/${getPath()}${locationHook.pathname}`;

  if (!resources) {
    return null;
  }
  const showWarning = warn === WarnMelding.RegisterSvarerIkke;
  const showInfo = info === InfoMelding.RegistreIkkeKomplett;

  const warningMessageComponetnt = showWarning ? (
    <>
      <NotificationPanel variant="warn" label={resources.clientOperation_GetRegisteroppforing_Warning_Title}>
        <p>{resources.clientOperation_GetRegisteroppforing_Warning_Body}</p>
      </NotificationPanel>
      <Spacer />
    </>
  ) : undefined;

  const InformationMessageComponent = showInfo ? (
    <>
      <NotificationPanel variant="info" label={resources.registeroppforing_InfoMessage_Title}>
        <p>{resources.registeroppforing_InfoMessage_Body}</p>
        <Spacer />
        {retryInnsynOrkestrator && (
          <Button
            variant="outline"
            onClick={(): void => {
              dispatch(fetchRegisterliste({ retryInnsynOrkestrator: true }));
            }}
          >
            {resources.retry_knapp}
          </Button>
        )}
      </NotificationPanel>
      {!showWarning && <Spacer />}
    </>
  ) : undefined;

  return (
    <PageRoot
      redirectTo={location}
      pageTitle={resources.page_helseregistre_title}
      pageDescription={resources.page_helseregistre_desc}
      hideHelpTrigger
    >
      <HelpExpanderStandalone hjelpetekstId="helpex-innsyn-og-bruk" />
      <Spacer />
      {!isAuthorized() && <Innloggingsknapp buttonText={resources.loginButtonText} />}
      <PageLoader
        resources={resources}
        error={error ? resources.default_ErrorMessage_Title : undefined}
        errorDescription={error ? resources.default_ErrorMessage_Body : undefined}
        loading={loading}
        inline
      >
        {InformationMessageComponent}
        {warningMessageComponetnt}
        <RegistreComponent registerliste={registerliste} resources={resources} />
      </PageLoader>
    </PageRoot>
  );
};

export default RegistreContainer;
